import { Badge, Button, Card, Flex, Input, Pagination } from 'antd';
import { DownOutlined, MinusOutlined, PlusOutlined, RightOutlined, ShoppingOutlined } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';

const { Search } = Input;

const getImageUrl = (variant) => {
  const image = variant?.image || variant?.images?.[0] || variant?.product?.thumbnail || variant?.product?.images?.[0];
  if (!image) return null;
  if (typeof image === 'string') return image;
  return image.sm_url || image.url || null;
};

const PressButton = ({ icon, onPress, ...props }) => {
  const holdTimeoutRef = useRef(null);
  const holdIntervalRef = useRef(null);

  const clearPressTimers = () => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearPressTimers();
  }, []);

  const startPress = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onPress();

    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        onPress();
      }, 120);
    }, 350);
  };

  const stopPress = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    clearPressTimers();
  };

  return (
    <Button
      {...props}
      icon={icon}
      style={{
        touchAction: 'manipulation',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...(props.style || {}),
      }}
      onPointerDown={startPress}
      onPointerUp={stopPress}
      onPointerLeave={stopPress}
      onPointerCancel={stopPress}
      onContextMenu={(event) => event.preventDefault()}
    />
  );
};

const QtyControl = React.memo(({ variantId, qty, addProduct, onLineQuantityChange, getCurrentQty }) => {
  if (qty === 0) {
    return null;
  }

  return (
    <Flex align='center' gap={4} onClick={(event) => event.stopPropagation()} style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
      <PressButton
        size='small'
        icon={<MinusOutlined />}
        onPress={() => {
          const currentQty = getCurrentQty(variantId);
          onLineQuantityChange(variantId, currentQty - 1);
        }}
      />
      <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 600, userSelect: 'none', WebkitUserSelect: 'none' }}>{qty}</span>
      <PressButton size='small' icon={<PlusOutlined />} onPress={() => addProduct(variantId)} />
    </Flex>
  );
});

const ProductCard = React.memo(({ productName, variants, qtyByVariantId, addProduct, onLineQuantityChange, getCurrentQty }) => {
  const [expanded, setExpanded] = useState(false);
  const isSingle = variants.length === 1;
  const totalQty = variants.reduce((sum, variant) => sum + (qtyByVariantId[variant.id] ?? 0), 0);
  const imageUrl = getImageUrl(variants[0]);

  return (
    <div style={{ borderRadius: 8, border: '1px solid #f0f0f0', overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{ position: 'relative', height: 110, background: '#f5f5f5', flexShrink: 0, cursor: isSingle ? (totalQty === 0 ? 'pointer' : 'default') : 'pointer' }}
        onClick={isSingle ? (totalQty === 0 ? () => addProduct(variants[0].id) : undefined) : () => setExpanded((current) => !current)}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={productName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Flex align='center' justify='center' style={{ width: '100%', height: '100%' }}>
            <ShoppingOutlined style={{ fontSize: 32, color: '#d9d9d9' }} />
          </Flex>
        )}
        {totalQty > 0 && (
          <Badge count={totalQty} size='small' color='blue' style={{ position: 'absolute', top: 6, right: 6 }} />
        )}
        {!isSingle && (
          <div style={{ position: 'absolute', bottom: 4, right: 6, color: '#aaa', fontSize: 10 }}>
            {expanded ? <DownOutlined /> : <RightOutlined />}
          </div>
        )}
      </div>

      {isSingle ? (
        <Flex
          align='center'
          justify='space-between'
          style={{ padding: '6px 8px', flex: 1, cursor: totalQty === 0 ? 'pointer' : 'default' }}
          onClick={totalQty === 0 ? () => addProduct(variants[0].id) : undefined}
        >
          <span style={{ fontSize: 12, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 6 }}>
            {productName}
          </span>
          {totalQty > 0 && (
            <QtyControl
              variantId={variants[0].id}
              qty={qtyByVariantId[variants[0].id] ?? 0}
              addProduct={addProduct}
              onLineQuantityChange={onLineQuantityChange}
              getCurrentQty={getCurrentQty}
            />
          )}
        </Flex>
      ) : (
        <>
          <div
            style={{ padding: '6px 8px', fontSize: 12, fontWeight: 500, cursor: 'pointer', borderBottom: expanded ? '1px solid #f0f0f0' : 'none' }}
            onClick={() => setExpanded((current) => !current)}
          >
            {productName}
          </div>
          {expanded && (
            <div>
              {variants.map((variant) => {
                const variantQty = qtyByVariantId[variant.id] ?? 0;

                return (
                  <Flex
                    key={variant.id}
                    align='center'
                    justify='space-between'
                    style={{ padding: '5px 8px 5px 14px', borderBottom: '1px solid #fafafa', cursor: variantQty === 0 ? 'pointer' : 'default' }}
                    onClick={variantQty === 0 ? () => addProduct(variant.id) : undefined}
                  >
                    <span style={{ fontSize: 11, color: '#555' }}>{variant.variant_title || 'Mặc định'}</span>
                    {variantQty > 0 && (
                      <QtyControl
                        variantId={variant.id}
                        qty={variantQty}
                        addProduct={addProduct}
                        onLineQuantityChange={onLineQuantityChange}
                        getCurrentQty={getCurrentQty}
                      />
                    )}
                  </Flex>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  if (prevProps.productName !== nextProps.productName) return false;
  if (prevProps.variants !== nextProps.variants) return false;

  for (const variant of prevProps.variants) {
    const prevQty = prevProps.qtyByVariantId[variant.id] ?? 0;
    const nextQty = nextProps.qtyByVariantId[variant.id] ?? 0;
    if (prevQty !== nextQty) return false;
  }

  return true;
});

const ProductPickerPanelDesktop = ({ viewModel }) => {
  const {
    openProductPicker,
    setOpenProductPicker,
    setProductFilterValue,
    qtyByVariantId,
    addProduct,
    onLineQuantityChange,
    page,
    setPage,
    pagedGroups,
    productGroups,
    getCurrentQty,
    pageSize,
  } = viewModel;

  return (
    <>
      <Flex style={{ marginTop: 8 }} justify='flex-end'>
        {!openProductPicker && (
          <Button type='link' onClick={() => setOpenProductPicker(true)} style={{ width: 150 }}>
            Thêm sản phẩm
          </Button>
        )}
      </Flex>

      <Card
        title='Thêm sản phẩm'
        size='small'
        style={{ display: openProductPicker ? 'block' : 'none', marginTop: 8 }}
        extra={<Button type='link' onClick={() => setOpenProductPicker(false)}>Đóng</Button>}
      >
        <Search
          placeholder='Tìm sản phẩm...'
          allowClear
          onChange={(event) => setProductFilterValue('info', event.target.value)}
          style={{ marginBottom: 10 }}
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 8,
          }}
        >
          {pagedGroups.map((group) => (
            <ProductCard
              key={group.productId}
              productName={group.productName}
              variants={group.variants}
              qtyByVariantId={qtyByVariantId}
              addProduct={addProduct}
              onLineQuantityChange={onLineQuantityChange}
              getCurrentQty={getCurrentQty}
            />
          ))}
        </div>
        <Flex justify='center' style={{ marginTop: 10 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={productGroups.length}
            onChange={setPage}
            showSizeChanger={false}
            hideOnSinglePage
            size='small'
          />
        </Flex>
      </Card>
    </>
  );
};

export default ProductPickerPanelDesktop;